import React, { useState, useEffect } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import PanelActions from '../../components/dev/PanelActions';
import api from '../../services/api';
import './EarningsPanel.css';

/**
 * What this app earns.
 *
 * EVERY FIGURE COMES FROM FROZEN AGREED PRICES, not from the app's current price. A
 * grandfathered subscriber pays what they agreed to, so revenue computed from the
 * advertised price would overstate it the moment a developer raised theirs.
 *
 * CURRENCIES ARE NEVER SUMMED. Adding £4 to $4 produces a number that is wrong in both,
 * so an app selling in two currencies gets two rows and no total. The server refuses to
 * do the addition; this refuses to display one.
 *
 * TWO FIGURES, NOT RECONCILED. "Expected each period" is one period of every active
 * purchase — not lifetime. "Collected so far" is the paid invoices Stripe sent us, less
 * full refunds. Fees on both are today's rate (`platform_fee_basis`); the settled
 * history and the fee actually taken on each charge are the Stripe dashboard's.
 *
 * Amounts arrive pre-rendered per purchase (`pricing_display`); the per-currency totals
 * are the one place a figure is assembled here, and they are built from integer minor
 * units with the symbol the server supplied, never with a local currency formatter.
 */
export default function EarningsPanel({ appId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getEarnings(appId)
      .then(r => { if (!cancelled) { setData(r); setError(null); } })
      .catch(e => { if (!cancelled) setError(e?.message || 'Could not load earnings.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appId]);

  if (loading) return <div className="earnings">Loading earnings…</div>;
  if (error) return <div className="earnings earnings--error">{error}</div>;
  if (!data) return null;

  const byCurrency = data.recurring_by_currency || {};
  const currencies = Object.keys(byCurrency);
  const counts = data.counts || {};

  if (!data.pricing_display?.is_paid) {
    return (
      <div className="earnings earnings--free">
        Free apps have nothing to collect. Set a price on the Price page.
      </div>
    );
  }

  // One figure per currency, side by side, never added together. Each string is the
  // server's rendering (`display`), so this page formats no money of its own.
  const perCurrency = (store) => {
    const codes = Object.keys(store || {});
    return codes.length
      ? codes.map(code => <span key={code}>{store[code].display?.net ?? '—'}</span>)
      : '—';
  };
  const feeBps = data.platform_fee_bps;

  return (
    <div className="earnings">
      <PanelActions><MoneyLink /></PanelActions>

      <section className="earnings__band">
        <h4 className="earnings__title">Totals</h4>
        <p className="earnings__help">After the platform fee. Each currency is counted on its own.</p>
        <div className="earnings__stats">
          <div className="earnings__stat">
            <small>Paying people</small>
            <b>{counts.active ?? 0}</b>
          </div>
          <div className="earnings__stat">
            {/* One period of every active purchase, net of the fee: a subscription's
                own interval, a one-off once. Not lifetime. */}
            <small>Expected each period</small>
            <b className="earnings__multi">{perCurrency(byCurrency)}</b>
          </div>
          <div className="earnings__stat">
            {/* What cards were really charged, from the paid invoices Stripe sent us. */}
            <small>Collected so far</small>
            <b className="earnings__multi">{perCurrency(data.collected_by_currency)}</b>
          </div>
          <div className="earnings__stat">
            {/* Payout dates are Stripe's to say; the fee is ours, so it is the figure here. */}
            <small>Platform fee</small>
            <b>{feeBps == null ? '—' : feeBps > 0 ? `${(feeBps / 100).toFixed(feeBps % 100 ? 2 : 0)}%` : 'None'}</b>
          </div>
        </div>
        {currencies.length > 1 && (
          <p className="earnings__note">
            Shown per currency and not added together — a single total across
            currencies would be wrong in every one of them.
          </p>
        )}
        <Counts counts={counts} anyPaid={currencies.length > 0} />
        <p className="earnings__note">
          Expected is one period of every active purchase. Collected is what Stripe has
          charged, from your paid invoices, less full refunds. Both use today&rsquo;s
          platform fee — your Stripe dashboard has the fee actually taken and your payouts.
        </p>
      </section>

      <section className="earnings__band">
        <h4 className="earnings__title">Who pays</h4>
        <BuyerList purchases={data.purchases} truncated={data.purchases_truncated}>
          <p className="earnings__empty">
            Nobody has bought it yet.
            {counts.abandoned > 0 && (
              <> {counts.abandoned} checkout{counts.abandoned === 1 ? '' : 's'} started and
              did not finish — worth reading as a pricing or trust signal rather than a
              traffic one.</>
            )}
          </p>
        </BuyerList>
      </section>
    </div>
  );
}

/** The way to the account's money as a whole, from the panel head. */
function MoneyLink() {
  const inRouter = useInRouterContext();
  return inRouter
    ? <Link className="panel-link" to="/billing">Money →</Link>
    : <a className="panel-link" href="/billing">Money →</a>;
}

/** The states worth a second look: failing cards, cancellations, refunds, walk-aways. */
function Counts({ counts, anyPaid }) {
  const items = [
    counts.past_due > 0 && (
      <span key="pd" className="is-warn" title="Paid, then their card failed. Access is paused, not revoked.">
        {counts.past_due} payment{counts.past_due === 1 ? '' : 's'} failing
      </span>
    ),
    counts.canceled > 0 && <span key="c">{counts.canceled} cancelled</span>,
    counts.refunded > 0 && <span key="r">{counts.refunded} refunded</span>,
    counts.abandoned > 0 && anyPaid && (
      <span key="a" title="Started a checkout and did not complete it.">{counts.abandoned} abandoned</span>
    ),
  ].filter(Boolean);
  return items.length ? <p className="earnings__counts">{items}</p> : null;
}

const STATUS_LABEL = {
  active: 'Paid', past_due: 'Payment failing', canceled: 'Cancelled',
  refunded: 'Refunded',
};

/**
 * Who bought. A person is shown by the name they show (first name or username) — never
 * their email, which stays with the platform. A group purchase is named as the group,
 * with the member who pressed pay where it was recorded (purchases since migration 294).
 * Abandoned checkouts are left out: they are counted above, and nobody bought anything.
 */
function BuyerList({ purchases, truncated, children }) {
  const rows = (purchases || []).filter(p => p.status !== 'pending');
  if (rows.length === 0) return children;
  return (
    <div className="earnings__buyers">
      <table>
        <thead>
          <tr><th>Buyer</th><th>Price</th><th>Status</th><th>Since</th></tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id}>
              <td>
                {p.buyer?.name || 'Unknown'}
                {p.buyer?.kind === 'group' && (
                  <small>
                    {' · group'}
                    {p.buyer.purchased_by?.name && ` · bought by ${p.buyer.purchased_by.name}`}
                  </small>
                )}
              </td>
              <td>{p.pricing_display?.headline || '—'}</td>
              <td>
                <span className={`earnings__pill earnings__pill--${p.status}`}>
                  {STATUS_LABEL[p.status] || p.status}
                </span>
              </td>
              <td>{formatDate(p.paid_at || p.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && <p className="earnings__note">Showing the latest 100.</p>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
